import React from "react";
import classNames from "classnames";
import { PinIcon, PinOpenIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Separator } from "@design-system/Separator/Separator";
import type { DatePickerPreset } from "./DatePicker.types";
import { getPresetId, splitPresetsByPinned } from "./DatePicker.lib";
import styles from "./DatePicker.module.scss";

const PresetRow = <TValue,>({
    preset,
    isActive,
    isPinned,
    onSelect,
    onTogglePin,
}: {
    preset: DatePickerPreset<TValue>;
    isActive: boolean;
    isPinned: boolean;
    onSelect: () => void;
    onTogglePin?: () => void;
}) => (
    <div
        className={classNames(styles.preset, {
            [styles.activePreset]: isActive,
            [styles.presetRow]: onTogglePin !== undefined,
        })}
    >
        <button
            type="button"
            className={styles.presetSelect}
            onClick={onSelect}
        >
            <span className={styles.presetLabel}>{preset.label}</span>
        </button>
        {onTogglePin && (
            <Button
                type="button"
                variant="secondary"
                size="sm"
                iconOnly
                className={classNames(styles.pinButton, {
                    [styles.showOnHover]: !isPinned,
                })}
                aria-label={isPinned ? "Unpin preset" : "Pin preset"}
                aria-pressed={isPinned}
                onClick={onTogglePin}
            >
                {isPinned ? <PinIcon /> : <PinOpenIcon />}
            </Button>
        )}
    </div>
);

export const DatePickerPresets = <TValue,>({
    presets,
    activeValue,
    isEqual,
    onSelect,
    pinnedPresetIds,
    onTogglePin,
    selectedPresetId,
}: {
    presets: ReadonlyArray<DatePickerPreset<TValue>>;
    activeValue: TValue;
    isEqual: (first: TValue, second: TValue) => boolean;
    onSelect: (value: TValue, presetId?: string) => void;
    pinnedPresetIds?: ReadonlySet<string>;
    onTogglePin?: (presetId: string) => void;
    selectedPresetId?: string | null;
}) => {
    const { pinnedPresets, unpinnedPresets } = pinnedPresetIds
        ? splitPresetsByPinned(presets, pinnedPresetIds)
        : { pinnedPresets: [], unpinnedPresets: presets };

    const renderRow = (preset: DatePickerPreset<TValue>, isPinned: boolean) => {
        const presetId = getPresetId(preset);
        const isActive =
            selectedPresetId !== undefined
                ? presetId === selectedPresetId
                : isEqual(activeValue, preset.value);
        return (
            <PresetRow
                key={presetId}
                preset={preset}
                isActive={isActive}
                isPinned={isPinned}
                onSelect={() => onSelect(preset.value, presetId)}
                onTogglePin={
                    onTogglePin ? () => onTogglePin(presetId) : undefined
                }
            />
        );
    };

    return (
        <div className={styles.presets} aria-label="Date presets">
            {pinnedPresets.length > 0 && (
                <>
                    <span className={styles.presetsLabel}>Pinned</span>
                    {pinnedPresets.map((preset) => renderRow(preset, true))}
                    <Separator className={styles.presetsSeparator} />
                </>
            )}
            {unpinnedPresets.map((preset) => renderRow(preset, false))}
        </div>
    );
};
